import {
    ScheduleApplicationPeriodRole,
    ScheduleApplicationPeriodStatus,
} from "@common/types/domains/accounting/schedule";

export interface LiveLadderPeriodInput {
    status: ScheduleApplicationPeriodStatus;
    reversedByPeriodId: string | null;
    role: ScheduleApplicationPeriodRole;
}

export interface LadderPeriodRow {
    id: string;
    role: ScheduleApplicationPeriodRole;
    status: ScheduleApplicationPeriodStatus;
    reversesPeriodId: string | null;
}

/**
 * A ladder month that still counts: not voided and not cancelled by a reversal
 * row (a reverse-mode change keeps the reversed original posted as history).
 */
export function isLiveLadderPeriod(period: LiveLadderPeriodInput): boolean {
    return (
        period.role === ScheduleApplicationPeriodRole.ladder &&
        period.status !== ScheduleApplicationPeriodStatus.voided &&
        period.reversedByPeriodId === null
    );
}

/**
 * The posted reversal row cancelling each reversed row, keyed by the reversed
 * row's id; a row has at most one reversal row.
 */
export function selectReversalRowIdsByReversedPeriodId<
    T extends LadderPeriodRow,
>(periods: readonly T[]): Map<string, string> {
    return new Map(
        periods.flatMap((period) =>
            period.role === ScheduleApplicationPeriodRole.reversal &&
            period.status === ScheduleApplicationPeriodStatus.posted &&
            period.reversesPeriodId !== null
                ? [[period.reversesPeriodId, period.id]]
                : [],
        ),
    );
}

export type LadderPeriodWithReversal<T extends LadderPeriodRow> = T &
    Pick<LiveLadderPeriodInput, "reversedByPeriodId">;

/**
 * Pairs every row with the posted reversal row that cancels it, so stored rows
 * read as listed rows.
 */
export function withReversedByPeriodId<T extends LadderPeriodRow>(
    periods: readonly T[],
): Array<LadderPeriodWithReversal<T>> {
    const reversalRowIds = selectReversalRowIdsByReversedPeriodId(periods);
    return periods.map((period) => ({
        ...period,
        reversedByPeriodId: reversalRowIds.get(period.id) ?? null,
    }));
}

/**
 * The live ladder rows out of every row of an application; reversal and
 * lifecycle rows are never ladder months.
 */
export function selectLiveLadderPeriods<T extends LadderPeriodRow>(
    periods: readonly T[],
): T[] {
    const liveIds = new Set(
        withReversedByPeriodId(periods)
            .filter(isLiveLadderPeriod)
            .map((period) => period.id),
    );
    return periods.filter((period) => liveIds.has(period.id));
}
